import { useEffect } from 'preact/hooks';
import { Link, PlainText } from '@/features/elements';
import { getImageUri } from '@/utils';
import { useConfigStore, useFormProps } from '../stores/ConfigStore';
import { useFormStore } from '../stores/FormStore';
import { useBlockStore } from '../stores/BlockStore';
import SvgIcon from './SvgIcon';
import { Fragment } from 'preact';

const customPageBlockMap = {
	text: PlainText,
	icon: SvgIcon,
	link: Link,
	img: 'img'
};

export default function CustomPage({ isFirstPage, isLastPage, ...props }) {
	const blockState = useBlockStore();
	const config = useConfigStore();
	const { webView } = useFormProps();
	const { actions } = useFormStore();

	const customPageElements =
		(isFirstPage
			? config.properties?.introPage?.elements
			: config.properties?.thanksPage?.elements) || {};
	const elementKeysArr =
		customPageElements && typeof customPageElements === 'object'
			? Object.keys(customPageElements)
			: [];
	const panelLength = elementKeysArr.filter(
		key => customPageElements[key]?.show_as_panels
	).length;

	const findTextBlocksKey = Object.keys(customPageElements).length
		? Object.keys(customPageElements).filter(
				el => customPageElements[el].type === 'text'
		  )[0]
		: null;

	const firstTextBlockId = customPageElements[findTextBlocksKey]?.genID ?? null;

	useEffect(() => {
		if (firstTextBlockId) {
			actions.updateScreenReaderHelper(
				customPageElements[findTextBlocksKey]?.value ?? 'Thank you for your feedback'
			);
		}
	}, []);

	return (
		<div
			className={isFirstPage ? 'front-page' : 'last-page'}
			id={isFirstPage ? 'frontPage' : 'lastPage'}
			data-testid={isFirstPage ? 'front-page' : 'last-page'}
		>
			{elementKeysArr.length > 0 &&
				elementKeysArr.map(elementKey => {
					const el = customPageElements[elementKey];
					const BlockComponent = customPageBlockMap[el.type];

					if (!BlockComponent) {
						return null;
					}

					const BlockProps = {
						...(el.type === 'text' && {
							text: el.value
						}),
						...(el.type === 'icon' && {
							icon: el.icon,
							alt: el?.alt
						}),
						...(el.type === 'img' && {
							src: getImageUri(el?.image?.url ?? el.url),
							title: el?.image?.alt ?? el?.alt ?? '',
							alt: el?.image?.alt ?? el?.alt ?? '',
							style: {
								height: el.layout === 'static' ? el.height : 'auto',
								width: el.width ?? '100%'
							}
						}),
						...(el.type === 'link' && {
							block: {
								id: el.genID,
								properties: {
									linktype: el.linktype || (el.use_as !== 'link' ? 'function' : 'link'),
									linktype_function: el.linktype_function || el.use_as,
									plaintext: el.value || el.description, // classic || raspberry
									description: el.value || el.description,
									hyperlinktext: el.hyperlinktext,
									show_as_buttons: el.show_as_buttons,
									show_as_panels: el.show_as_panels,
									close_on_linkclick:
										el.close_on_linkclick !== 'undefined' ? el.close_on_linkclick : false,
									icon: el.icon,
									iconAlignment: el.icon_align,
									hyperlinkhref: el.hyperlinkhref
								}
							}
						})
					};

					const panelType = el.show_as_panels
						? 'panel-wrap'
						: el.show_as_buttons
						? 'btn-wrap'
						: '';
					const imgType =
						el.type === 'img' ? `img-${el.layout ? el.layout : 'responsive'}` : '';

					const displayAs = el.show_as_panels ? 'inline-block' : 'block';

					const divStyle = {
						textAlign: el.align,
						display: isFirstPage
							? displayAs
							: blockState[el.genID]?.isVisible
							? displayAs
							: 'none',
						width: !el.show_as_panels
							? 'auto'
							: panelLength === 1
							? '100%'
							: panelLength % 2 === 0
							? '50%'
							: '33%'
					};

					return (
						<div
							className={`custom-section custompage-${el.type} ${panelType} ${imgType}`}
							id={`${isFirstPage ? 'intro-page' : 'thanks-page'}_${
								el.genID ?? elementKey
							}`}
							style={divStyle}
						>
							<BlockComponent {...BlockProps} />
						</div>
					);
				})}
			{webView && elementKeysArr.length === 0 && isLastPage && (
				<Fragment>
					<div class="completed-anim do-anim">
						<SvgIcon
							icon="check"
							alt="check icon"
						/>
					</div>
					<span
						dangerouslySetInnerHTML={{ __html: config.properties.exit_content }}
					></span>
				</Fragment>
			)}
		</div>
	);
}
